import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Pendencias } from '../Pendencias';
import { ErroApi } from '../api';
import {
  descartarMassaEspecificaDeterminacao, descartarMassaEspecificaUmidade,
  gravarMassaEspecificaCabecalho, gravarMassaEspecificaDeterminacoes,
  gravarMassaEspecificaUmidades, verMassaEspecifica,
} from '../api/massa-especifica';
import type { EnsaioMassaEspecifica } from '../api/massa-especifica';
import {
  AncoraDePendencias, BlocoDaFicha, CabecalhoDaFicha, EscolhaDeBalanca,
  EtapasDaFicha, FaixaDaFicha, JanelaDeDescarte, LaudoDaFicha, RodapeDeEtapas,
  fichaCongelada, useEquipamentos, useEtapa, useFicha,
} from '../Ficha';
import type { EquipamentoDaLista, Etapa, Indicador } from '../Ficha';
import { numero, paraNumero, plural, rotulo } from '../formato';
import { FotosDoRegistro } from '../FotosDoRegistro';
import { useSessao } from '../sessao';
import {
  Aviso, Campo, CampoNumerico, Carregando, ErroDaApi, Escolha,
  EscolhaPorCodigo, RespostaDaAcao, Selo, TabelaDeLancamento, comoCodigo,
} from '../ui';
import type { ColunaDeLancamento, LinhaDeLancamento, Opcao } from '../ui';
import { useEstufaDoEnsaio } from './NaEstufa';
import { QuadroCapsulas } from './QuadroCapsulas';
import { AvisoDaRepeticaoAberta } from './Triviais';
import { useLinhasDaRepeticao } from './repeticao/Repeticao';
import type { UseRepeticao } from './repeticao/Repeticao';
import {
  LinhasEmLeitura, RepeticaoNumerica, ResultadoVigente,
} from './repeticao/RepeticaoNumerica';
import type { ResultadoNumerico } from './repeticao/RepeticaoNumerica';
import { TentativasAnteriores } from './repeticao/TentativasAnteriores';
import { TrilhaDoEnsaio } from './repeticao/TrilhaDoEnsaio';

/**
 * Lançar a massa específica dos sólidos — ABNT NBR 17212:2025.
 *
 * ─── No Carbon (onda 236) ──────────────────────────────────────────────
 *
 * Quatro etapas, uma por tela: condições, umidade da amostra,
 * determinações no picnômetro, fotos e laudo. São DUAS baterias de
 * pesagem — as cápsulas da umidade e o picnômetro —, e cada uma tem a
 * sua balança (RN-215), escolhida uma vez para a bateria.
 *
 * O resultado sai com três algarismos significativos (seção 6) — duas
 * casas para os valores de solo, arredondado pelo BANCO; a tela só
 * formata o que vem.
 *
 * ─── O que a tela NÃO pede ─────────────────────────────────────────────
 *
 * M1 — a massa do picnômetro cheio de água. Ela vem da CURVA de
 * calibração do picnômetro (a parábola ajustada aos pontos do
 * certificado, vigente na data do ensaio), na temperatura do ensaio,
 * como a 5.2.15 manda; fora da faixa medida não se extrapola. O campo de
 * M1 declarado é exceção (RN-259): só aparece para quem tem «Corrigir
 * lançamento travado», e a tela exige o certificado e o motivo junto —
 * um número digitado sem origem é um buraco na rastreabilidade
 * exatamente onde a calibração deveria estar.
 *
 * E não pede ρw(T): é a Tabela A.1 do Anexo A da norma, que mora no
 * banco desde a migração `0049`.
 */

const CAT_CAPSULA = ['capsula'];
const CAT_PICNOMETRO = ['picnometro'];

const num = (v: unknown, casas: number) => numero(v, casas);

export function MassaEspecifica() {
  const { id = '' } = useParams();
  const [d, setD] = useState<EnsaioMassaEspecifica | null>(null);
  const equip = useEquipamentos();
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);
  const ficha = useFicha(id, 'NBR 17212', versao);
  /* As cápsulas na estufa e o carimbo das pesagens (onda 294, RN-241). */
  const estufa = useEstufaDoEnsaio(id, versao);
  /* R2 (onda 323): com a repetição aberta, as etapas de lançamento listam e
     numeram as linhas DELA. */
  const rep = useLinhasDaRepeticao<EnsaioMassaEspecifica>(
    id, 'massa-especifica', d?.repeticaoAberta, versao);
  const { pode } = useSessao();
  /* A Interpretação (onda 323) existe só para quem interpreta. */
  const interpreta = pode('ensaio.interpretar');
  const total = interpreta ? 5 : 4;

  const carregar = useCallback(async () => {
    try { setD(await verMassaEspecifica(id)); } catch (e) { setErro(e); }
  }, [id]);
  useEffect(() => { void carregar(); }, [carregar]);

  const gravar = useCallback(async (
    marca: string, chamada: () => Promise<EnsaioMassaEspecifica>,
  ) => {
    setSalvando(marca); setErro(null); setOk(null);
    try {
      setD(await chamada());
      setOk('Gravado.');
      setVersao((v) => v + 1);
      return true;
    } catch (e) { setErro(e); return false; }
    finally { setSalvando(null); }
  }, []);

  const opcoes = (cats: string[]): Opcao[] => equip
    .filter((e) => cats.includes(e.categoria))
    /* `detalhe` alimenta o filtro do `EscolhaPorCodigo`: quem digita
       «118» acha CAP-0118, e quem digita «cbr» acha os cilindros. */
    .map((e) => ({ valor: e.id, texto: e.codigo_patrimonial,
      detalhe: e.nome }));

  const campoErro = (c: string) =>
    erro instanceof ErroApi ? erro.campo(c) : undefined;

  const c = d?.cabecalho;
  const temCondicoes = c != null;
  const temUmidade = c?.umidade_higroscopica != null
    && (!!d?.balancaUmidade || (d?.umidades ?? []).length === 0);
  const dets: number = c?.determinacoes ?? 0;
  const temDeterminacoes = c?.massa_especifica_solidos != null && !!d?.balanca;
  const [etapa, irPara] = useEtapa(total,
    !temCondicoes ? 1 : !temUmidade ? 2 : !temDeterminacoes ? 3 : total);

  if (erro && !d) return <ErroDaApi erro={erro} />;
  if (!d) return <Carregando texto="Abrindo o ensaio" />;
  const congelado = fichaCongelada(ficha?.status);
  /* O que as etapas de lançamento mostram: a repetição aberta, se houver (R2). */
  const repAberta = d.repeticaoAberta != null;
  const daBancada: EnsaioMassaEspecifica = repAberta && rep.atual
    ? { ...d, umidades: rep.atual.umidades ?? [], determinacoes: rep.atual.determinacoes ?? [],
        balanca: rep.atual.balanca ?? d.balanca,
        balancaUmidade: rep.atual.balancaUmidade ?? d.balancaUmidade,
        cabecalho: rep.atual.cabecalho ?? d.cabecalho }
    : d;
  const cb = daBancada.cabecalho;
  const umidadesValidas = (daBancada.umidades ?? []).filter((u) => !u.descartada).length;
  const bDets: number = cb?.determinacoes ?? 0;
  const bValidas = validasDaMes(cb);
  const naTentativa = repAberta ? ` · tentativa ${d.repeticaoAberta}` : '';

  const etapas: Etapa[] = [
    { nome: 'Condições', feita: temCondicoes,
      resumo: c?.preparacao ? rotulo(c.preparacao).toLowerCase() : 'a gravar' },
    { nome: 'Umidade da amostra', feita: repAberta ? cb?.umidade_higroscopica != null : temUmidade,
      falta: umidadesValidas > 0 && !daBancada.balancaUmidade,
      resumo: umidadesValidas
        ? `${plural(umidadesValidas, 'cápsula', 'cápsulas')}${naTentativa}`
        : c?.umidade_higroscopica_informada != null ? 'transcrita' : 'a lançar' },
    { nome: 'Determinações',
      feita: repAberta ? cb?.massa_especifica_solidos != null : temDeterminacoes,
      falta: bDets > 0 && !daBancada.balanca,
      resumo: `${plural(bValidas, 'válida', 'válidas')}${naTentativa}` },
    ...(interpreta ? [{
      nome: 'Interpretação', feita: temDeterminacoes && !repAberta,
      resumo: repAberta ? `repetindo · tentativa ${d.repeticaoAberta}`
        : c?.massa_especifica_solidos != null
          ? `ρs ${num(c.massa_especifica_solidos, RESULTADO_MES.casas)} g/cm³` : 'depois das determinações',
    }] : []),
    { nome: 'Fotos e laudo', feita: ficha?.status === 'aprovado' },
  ];

  const indicadores: Indicador[] = [
    { rotulo: 'ρs · massa específica dos sólidos', chave: true, unidade: 'g/cm³',
      valor: c?.massa_especifica_solidos != null
        ? num(c.massa_especifica_solidos, 2) : null,
      porque: c?.situacao ? rotulo(c.situacao) : undefined,
      como: <>A média das determinações válidas pela equação 5.2.16, com
        M<sub>1</sub> da curva do picnômetro e ρ<sub>w</sub>(T) da Tabela
        A.1; três algarismos significativos (NBR 17212:2025, seção 6).</> },
    { rotulo: 'Determinações válidas', valor: String(dets), nota: 'mínimo de 2' },
    { rotulo: 'Amplitude', unidade: 'g/cm³',
      valor: c?.amplitude != null ? num(c.amplitude, 4) : null,
      nota: c?.amplitude != null
        ? (c.amplitude_conforme ? 'até 0,02 — satisfatório' : 'acima de 0,02')
        : undefined,
      como: 'A maior menos a menor entre as válidas. A seção 6 só considera o ensaio satisfatório até 0,02 g/cm³.' },
    { rotulo: 'w · umidade da amostra', unidade: '%',
      valor: c?.umidade_higroscopica != null ? num(c.umidade_higroscopica, 2) : null,
      porque: 'Lance as cápsulas na etapa 2, ou transcreva na 1.' },
  ];

  return (
    <div className="tela massa-especifica ficha-carbon">
      <CabecalhoDaFicha id={id} sigla="MES" titulo="Massa específica dos sólidos"
        ficha={ficha} normaDaTela="ABNT NBR 17212"
        tentativaAberta={d?.repeticaoAberta} />
      <FaixaDaFicha indicadores={indicadores} itens={d.pendencias}
        pendencias={d.pendenciasDetalhadas} />
      <EtapasDaFicha etapas={etapas} atual={etapa} irPara={irPara} />

      {/* O componente é o mesmo das outras fichas (0090/0091): um
        * segundo desenho da mesma coisa é como duas telas passam a dizer
        * coisas diferentes sobre a mesma regra. */}
      <AncoraDePendencias>
        <Pendencias itens={d.pendencias} id={id}
          detalhe={d.pendenciasDetalhadas} aoJustificar={carregar} />
      </AncoraDePendencias>

      <RespostaDaAcao erro={erro} ok={ok ?? null} />
      {congelado && (
        <Aviso tipo="info" titulo={`Ensaio ${rotulo(ficha?.status).toLowerCase()} — só leitura`}>
          {ficha?.status === 'aprovado'
            ? 'Dado de ensaio aprovado não se altera. Correção é nova revisão: peça a reabertura a quem aprova.'
            : 'Ensaio encerrado não recebe dado novo. Se o serviço for refeito, é ensaio novo.'}
        </Aviso>
      )}
      {repAberta && etapa <= 3 && (
        <AvisoDaRepeticaoAberta numero={d.repeticaoAberta!} interpreta={interpreta} />
      )}

      <fieldset className="so-leitura" disabled={congelado}>
      {etapa === 1 && (
        <Condicoes c={c} id={id} gravar={gravar} salvando={salvando}
          campoErro={campoErro} />
      )}

      {(etapa === 2 || etapa === 3) && repAberta && rep.carregando && (
        <Carregando texto="Lendo as linhas da repetição" />
      )}

      {etapa === 2 && !(repAberta && rep.carregando) && (
        <QuadroCapsulas key={repAberta ? `rep-${d.repeticaoAberta}` : 'vigente'}
          titulo="Umidade da amostra" rotuloLinha="Determinação"
          sub="É ela que converte a massa úmida pesada na bancada em Ms, a massa seca da equação 5.2.16."
          comGolpes={false} linhas={daBancada.umidades ?? []}
          opcoesCapsula={opcoes(CAT_CAPSULA)} equipamentos={equip}
          balanca={daBancada.balancaUmidade} salvando={salvando}
          erroBalanca={campoErro('balancaEquipamentoId')}
          aoGravar={({ balancaEquipamentoId, linhas }) => gravar('linhas',
            () => gravarMassaEspecificaUmidades(id,
              { balancaEquipamentoId, determinacoes: linhas }))}
          aoDescartar={(uid, motivo) => gravar('descarte',
            () => descartarMassaEspecificaUmidade(id, uid, motivo))}
          estufa={estufa} congelado={congelado}
          aoMudou={async () => { await carregar(); setVersao((v) => v + 1); }}
        />
      )}

      {etapa === 3 && !(repAberta && rep.carregando) && (
        <>
          <Determinacoes key={repAberta ? `rep-${d.repeticaoAberta}` : 'vigente'}
            d={daBancada} id={id} gravar={gravar} salvando={salvando}
            campoErro={campoErro} opcoesPicnometro={opcoes(CAT_PICNOMETRO)}
            equipamentos={equip} />
          {cb?.situacao === 'amplitude_excedida' && (
            <Aviso tipo="atencao" titulo="Fora do critério da norma">
              A seção 6 da NBR 17212:2025 só considera os ensaios satisfatórios
              quando os resultados não diferem entre si em mais que 0,02 g/cm³.
            </Aviso>
          )}
          {Number(cb?.fora_da_faixa_calibrada ?? 0) > 0 && (
            <Aviso tipo="atencao" titulo="M₁ declarado, não lido na curva">
              Há determinação com M₁ declarado à mão (exceção, RN-259): ele
              vale mais que a curva do picnômetro. Confira o certificado e o
              motivo registrados.
            </Aviso>
          )}
        </>
      )}

      {interpreta && etapa === 4 && (
        <>
          {/* A repetição na Interpretação (onda 323, RN-260, D7: os números). */}
          <RepeticaoMes id={id} d={d} congelado={congelado} opcoesPicnometro={opcoes(CAT_PICNOMETRO)}
            aoMudar={async () => { await carregar(); setVersao((v) => v + 1); }} />
          <TentativasAnteriores ensaioId={id} ficha="massa-especifica" versao={versao}
            render={(r: any) => <LinhasEmLeitura linhas={r?.determinacoes ?? []} f={r}
              colunas={COLUNAS_DA_MES} linha={linhaDaMes} rotulo="Determinações da tentativa" />} />
          <TrilhaDoEnsaio ensaioId={id} versao={versao} />
        </>
      )}

      {etapa === total && (
        <>
          <FotosDoRegistro
            id={id}
            titulo="Registro fotográfico"
            dica={'O picnômetro montado, a amostra, o que ajudar a conferir depois.'}
            roteiro={[]}
          />
          <LaudoDaFicha id={id} />
        </>
      )}
      </fieldset>

      <RodapeDeEtapas etapas={etapas} atual={etapa} irPara={irPara}
        nota={ok ? 'Gravado — o resultado acima já é o do servidor.' : undefined} />
    </div>
  );
}

interface GravarFn {
  (marca: string, chamada: () => Promise<EnsaioMassaEspecifica>):
  Promise<boolean>;
}

// ── 1 · condições ─────────────────────────────────────────────────────

function Condicoes({ c, id, gravar, salvando, campoErro }: {
  c: any; id: string; gravar: GravarFn; salvando: string | null;
  campoErro(x: string): string | undefined;
}) {
  const [preparacao, setPreparacao] = useState<string | null>(null);
  const [wInf, setWInf] = useState('');
  const [obs, setObs] = useState('');

  useEffect(() => {
    setPreparacao(c?.preparacao ?? 'sem_secagem_previa');
    setWInf(c?.umidade_higroscopica_informada != null
      ? String(c.umidade_higroscopica_informada) : '');
    setObs(c?.observacoes ?? '');
  }, [c?.preparacao, c?.umidade_higroscopica_informada, c?.observacoes]);

  const enviar = async () => {
    await gravar('cabecalho', () => gravarMassaEspecificaCabecalho(id, {
      preparacao,
      umidadeHigroscopicaInformada: paraNumero(wInf) ?? undefined,
      observacoes: obs || undefined,
    }));
  };

  return (
    <BlocoDaFicha titulo="Condições do ensaio"
      sub="A seção 6 da norma manda indicar a preparação no relatório.">
      <div className="fe-miolo fe-campos">
        <Escolha rotulo="Preparação" valor={preparacao}
          aoEscolher={setPreparacao} limpavel={false}
          opcoes={[
            { valor: 'sem_secagem_previa', texto: 'sem secagem prévia' },
            { valor: 'com_secagem_previa', texto: 'com secagem prévia ao ar' },
          ]} />
        <Campo rotulo="Umidade da amostra transcrita (%)" inputMode="decimal"
          value={wInf} onChange={(e) => setWInf(e.target.value)}
          erro={campoErro('umidadeHigroscopicaInformada')}
          dica="Só quando a umidade veio de outro ensaio. Havendo cápsulas
                lançadas na etapa 2, elas mandam." />
        <Campo rotulo="Observações" value={obs}
          onChange={(e) => setObs(e.target.value)} />
      </div>
      <div className="fe-acoes-bloco">
        <button type="button" className="principal" onClick={() => void enviar()}
          disabled={salvando !== null}>
          {salvando === 'cabecalho' ? 'Gravando…' : 'Gravar condições'}
        </button>
      </div>
    </BlocoDaFicha>
  );
}

// ── 3 · as determinações ──────────────────────────────────────────────

interface LinhaDet {
  picnometro: string; umida: string; m2: string; temperatura: string;
  m1: string; certificado: string; motivo: string;
}
const detVazia = (): LinhaDet => ({
  picnometro: '', umida: '', m2: '', temperatura: '', m1: '', certificado: '',
  motivo: '',
});

/** O motivo de M₁ declarado precisa dizer algo (o banco recusa menos de 10 letras, P-TXT-10). */
const MOTIVO_MINIMO = 10;

/**
 * De onde veio M₁ — o selo e o título que prova a conta (RN-259).
 *
 * O texto nunca é só cor: «Curva», «Declarado», «Fora da faixa» e «Sem
 * curva» dizem o que houve, e o título traz os números para quem quer
 * conferir contra o certificado.
 */
function SeloDeOrigemM1({ x }: { x: any }) {
  const m1 = `${num(x.massa_picnometro_agua, 2)} g`;
  const rho = `ρw = ${num(x.agua_massa_especifica, 4)} g/cm³ (NBR 17212, Tabela A.1)`;
  const t = `${num(x.temperatura, 1)} °C`;
  switch (x.m1_origem) {
    case 'curva':
      return (
        <Selo tom="ok"
          titulo={`M₁ = ${m1} (${x.picnometro}, curva v${x.m1_curva_versao}, ${t}) · ${rho}`}>
          Curva
        </Selo>
      );
    case 'declarada':
      return (
        <Selo tom="atencao"
          titulo={`M₁ = ${m1} declarado à mão, não lido na curva (exceção, RN-259)${
            x.certificado_m1 ? ` · certificado ${x.certificado_m1}` : ''}${
            x.motivo_m1_declarado ? ` · motivo: ${x.motivo_m1_declarado}` : ''} · ${rho}`}>
          Declarado
        </Selo>
      );
    case 'fora_da_faixa':
      return (
        <Selo tom="critico"
          titulo={`T = ${t} está fora da faixa calibrada de ${x.picnometro} (curva v${
            x.m1_curva_versao}: ${num(x.m1_curva_x_min, 1)} a ${num(x.m1_curva_x_max, 1)} °C). `
            + 'A curva não extrapola: sem M₁ não há resultado.'}>
          Fora da faixa
        </Selo>
      );
    case 'sem_curva':
      return (
        <Selo tom="critico"
          titulo={x.m1_curva_id
            ? `${x.picnometro} tem curva vigente com menos de 4 pontos, que não se ajusta. Sem M₁ não há resultado.`
            : `${x.picnometro} não tinha curva de calibração vigente na data do ensaio. Sem M₁ não há resultado.`}>
          Sem curva
        </Selo>
      );
    default:
      return null;
  }
}

function Determinacoes({
  d, id, gravar, salvando, campoErro, opcoesPicnometro, equipamentos,
}: {
  d: EnsaioMassaEspecifica; id: string; gravar: GravarFn;
  salvando: string | null; campoErro(x: string): string | undefined;
  opcoesPicnometro: Opcao[]; equipamentos: EquipamentoDaLista[];
}) {
  const [novas, setNovas] = useState<LinhaDet[]>([detVazia(), detVazia()]);
  const [descartando, setDescartando] = useState<any | null>(null);
  /* M₁ digitado é exceção: só quem corrige lançamento travado (RN-259). */
  const { pode } = useSessao();
  const podeDeclarar = pode('ensaio.corrigir_lancamento');
  const [balancaId, setBalancaId] = useState(d.balanca?.id ?? '');
  useEffect(() => { setBalancaId(d.balanca?.id ?? ''); }, [d.balanca?.id]);
  const balancaMudou = balancaId !== '' && balancaId !== (d.balanca?.id ?? '');

  const mexer = (i: number, campo: keyof LinhaDet, v: string) =>
    setNovas((ls) => ls.map((l, j) => (j === i ? { ...l, [campo]: v } : l)));

  const proxima = useMemo(
    () => (d.determinacoes ?? []).reduce(
      (m, x) => Math.max(m, Number(x.ordem)), 0) + 1,
    [d.determinacoes]);

  const prontas = novas
    .map((l, i) => ({
      ordem: proxima + i,
      picnometroEquipamentoId: l.picnometro,
      massaAmostraUmida: paraNumero(l.umida),
      massaPicnometroSoloAgua: paraNumero(l.m2),
      temperatura: paraNumero(l.temperatura),
      massaPicnometroAguaDeclarada: podeDeclarar ? paraNumero(l.m1) ?? undefined : undefined,
      certificadoM1: podeDeclarar ? l.certificado || undefined : undefined,
      motivoM1: podeDeclarar && l.m1 ? l.motivo.trim() || undefined : undefined,
    }))
    .filter((x) => x.picnometroEquipamentoId && x.massaAmostraUmida != null
      && x.massaPicnometroSoloAgua != null && x.temperatura != null);

  const falta = ((): string | null => {
    if (prontas.length && !balancaId) return 'Falta escolher a balança.';
    if (podeDeclarar && novas.some((l) => l.m1.trim() && paraNumero(l.m1) === undefined)) {
      return 'M₁ declarado não é um número: confira o valor digitado.';
    }
    if (prontas.some((x) => x.massaPicnometroAguaDeclarada != null
      && (!x.certificadoM1 || (x.motivoM1 ?? '').length < MOTIVO_MINIMO))) {
      return `M₁ declarado pede o certificado e o motivo (no mínimo ${MOTIVO_MINIMO} letras).`;
    }
    if (prontas.length || balancaMudou) return null;
    return 'Preencha o picnômetro, a amostra úmida, M₂ e a temperatura para gravar.';
  })();

  const enviar = async () => {
    const feito = await gravar('linhas',
      () => gravarMassaEspecificaDeterminacoes(id, {
        ...(balancaId ? { balancaEquipamentoId: balancaId } : {}),
        determinacoes: prontas.map((x) => ({ ...x, ordem: x.ordem })),
      } as any));
    if (feito) setNovas([detVazia(), detVazia()]);
  };

  const colunas: ColunaDeLancamento[] = [
    { texto: 'Nº' },
    { texto: 'Picnômetro' },
    { texto: 'Amostra úmida (g)', num: true },
    { texto: 'M₂ (g)', num: true },
    { texto: 'T (°C)', num: true },
    { texto: 'M₁ (g)', num: true },
    { texto: 'Origem de M₁' },
    { texto: 'ρs (g/cm³)', num: true },
    { texto: '' },
  ];

  const lancadas: LinhaDeLancamento[] = (d.determinacoes ?? []).map((x) => ({
    chave: x.id,
    classe: x.descartada ? 'descartada' : undefined,
    titulo: <>Determinação {x.ordem} · <span className="cod">{x.picnometro ?? '—'}</span></>,
    resultado: x.descartada ? '—'
      : <span className="n">{num(x.massa_especifica_solidos, 4)} g/cm³</span>,
    nota: x.descartada ? `Descartada: ${x.motivo_descarte ?? ''}` : undefined,
    semCartao: [0, 1, 7],
    celulas: [
      x.ordem,
      <span className="cod">{x.picnometro ?? '—'}</span>,
      num(x.massa_amostra_umida, 2),
      num(x.massa_picnometro_solo_agua, 2),
      num(x.temperatura, 1),
      num(x.massa_picnometro_agua, 2),
      <SeloDeOrigemM1 x={x} />,
      <b className="fe-w">{x.descartada ? '—' : num(x.massa_especifica_solidos, 4)}</b>,
      x.descartada
        ? <span className="fe-motivo" title={x.motivo_descarte}><Selo tom="neutro">Descartada</Selo></span>
        : (
          <button type="button" className="botao discreto pequeno fe-descartar"
            aria-label={`Descartar determinação ${x.ordem}`}
            disabled={salvando !== null}
            onClick={() => setDescartando(x)}>
            Descartar
          </button>
        ),
    ],
  }));

  const novasLinhas: LinhaDeLancamento[] = novas.map((l, i) => ({
    chave: `nova-${i}`,
    classe: 'nova',
    titulo: `Determinação ${proxima + i} · nova`,
    semCartao: [0, 6, 7],
    celulas: [
      <span className="sub">{proxima + i}</span>,
      <EscolhaPorCodigo
        rotulo=""
        opcoes={opcoesPicnometro.map(comoCodigo)}
        valor={l.picnometro}
        aoEscolher={(v) => mexer(i, 'picnometro', v)}
      />,
      <CampoNumerico rotulo="Amostra úmida (g)" rotuloOculto value={l.umida}
        onChange={(ev) => mexer(i, 'umida', ev.target.value)} />,
      <CampoNumerico rotulo="M₂ (g)" rotuloOculto value={l.m2}
        onChange={(ev) => mexer(i, 'm2', ev.target.value)} />,
      <CampoNumerico rotulo="Temperatura (°C)" rotuloOculto value={l.temperatura}
        onChange={(ev) => mexer(i, 'temperatura', ev.target.value)} />,
      podeDeclarar ? (
        <span className="fe-m1">
          <CampoNumerico rotulo="M₁ declarado (g)" rotuloOculto value={l.m1}
            placeholder="da curva"
            onChange={(ev) => mexer(i, 'm1', ev.target.value)} />
          {l.m1 && (
            <>
              <Campo rotulo="Certificado" value={l.certificado}
                placeholder="certificado"
                onChange={(ev) => mexer(i, 'certificado', ev.target.value)} />
              <Campo rotulo="Motivo do M₁ declarado" value={l.motivo}
                placeholder="por que não a curva"
                onChange={(ev) => mexer(i, 'motivo', ev.target.value)} />
            </>
          )}
        </span>
      ) : <span className="sub">da curva</span>,
      <span className="sub">—</span>,
      <span className="sub">—</span>,
      <button type="button" className="remover" tabIndex={-1}
        aria-label={`Remover determinação ${proxima + i}`}
        title="Remover esta linha (ainda não gravada)"
        onClick={() => setNovas((ls) => ls.filter((_, j) => j !== i))}>
        ×
      </button>,
    ],
  }));

  const validas = (d.determinacoes ?? []).filter((x) => !x.descartada).length;

  return (
    <BlocoDaFicha titulo="Determinações"
      selo={validas ? <Selo tom={validas >= 2 ? 'ok' : 'atencao'}>
        {plural(validas, 'válida', 'válidas')}</Selo> : undefined}
      sub={podeDeclarar
        ? <>M<sub>1</sub> vem da curva de calibração do picnômetro, na
          temperatura do ensaio, e não se extrapola. Declare M<sub>1</sub> só
          como exceção (RN-259): ele vale mais que a curva, e a tela pede o
          certificado e o motivo.</>
        : <>M<sub>1</sub> vem da curva de calibração do picnômetro, na
          temperatura do ensaio, e não se extrapola. Se a temperatura sair da
          faixa da curva, quem corrige lançamentos decide o que fazer.</>}>
      <div className="fe-miolo fe-campos">
        <EscolhaDeBalanca equipamentos={equipamentos} valor={balancaId}
          aoEscolher={setBalancaId} erro={campoErro('balancaEquipamentoId')} />
      </div>
      <div className="fe-tabela">
        <div className="fe-tabela-topo">
          <h3>Determinações <small>{(d.determinacoes ?? []).length
            ? `${(d.determinacoes ?? []).length} lançadas` : 'nenhuma lançada ainda'}</small></h3>
          <button type="button" className="botao discreto pequeno"
            onClick={() => setNovas((ls) => [...ls, detVazia()])}>
            + linha
          </button>
        </div>
        <TabelaDeLancamento rotulo="Determinações — lançadas e novas"
          classe="determinacoes fe-lanc" colunas={colunas}
          linhas={[...lancadas, ...novasLinhas]} />
        {campoErro('certificadoM1') && (
          <Aviso tipo="atencao">{campoErro('certificadoM1')}</Aviso>
        )}
        <div className="acoes fe-acoes">
          {falta
            ? <p className="o-que-falta">{falta}</p>
            : <p className="fe-lembrete">Linha em branco não sobe; a errada se descarta com motivo.</p>}
          <button type="button" className="principal" data-gravar-tabela=""
            aria-disabled={falta !== null || salvando !== null}
            title={falta ?? undefined}
            onClick={() => { if (!falta && salvando === null) void enviar(); }}>
            {salvando === 'linhas' ? 'Gravando…' : 'Gravar determinações'}
          </button>
        </div>
      </div>
      <JanelaDeDescarte
        alvo={descartando ? `determinação ${descartando.ordem} · ${descartando.picnometro ?? ''}` : null}
        aoFechar={() => setDescartando(null)}
        aoConfirmar={(motivo) => gravar('descarte',
          () => descartarMassaEspecificaDeterminacao(id, descartando.id, motivo))}
        salvando={salvando !== null}
      />
    </BlocoDaFicha>
  );
}

// ── 4 · a repetição na Interpretação (onda 323) ───────────────────────

/**
 * As determinações que dão ρs: a view conta em `determinacoes` todas as não
 * descartadas, inclusive as sem M₁ (fora da faixa, sem curva) e as sem tara,
 * que não têm ρs. Válida, na tela, é a que entra na média.
 */
export const validasDaMes = (c: any): number => Math.max(0,
  Number(c?.determinacoes ?? 0) - Number(c?.sem_m1 ?? 0) - Number(c?.sem_tara ?? 0));

/**
 * A comparação da repetição da MES: o ρs do servidor, com as casas do laudo
 * (o banco arredonda a 2 — NBR 17212, seção 6).
 */
const RESULTADO_MES: ResultadoNumerico = {
  rotulo: 'ρs', unidade: 'g/cm³', casas: 2,
  valor: (f) => (f?.cabecalho?.massa_especifica_solidos != null
    ? Number(f.cabecalho.massa_especifica_solidos) : null),
  nota: (f, lado) => {
    const c = f?.cabecalho;
    const n = validasDaMes(c);
    return plural(n, 'determinação válida', 'determinações válidas')
      + (lado === 'atual' ? ' (mínimo 2)' : '')
      + (c?.amplitude != null ? ` · amplitude ${num(c.amplitude, 4)}` : '')
      + (c?.situacao && c.situacao !== 'ok' ? ` · ${rotulo(c.situacao).toLowerCase()}` : '');
  },
  semDiferenca: 'sai com duas determinações válidas',
};

/** As colunas das determinações no picnômetro, em leitura. */
export const COLUNAS_DA_MES: ColunaDeLancamento[] = [
  { texto: 'Nº' },
  { texto: 'Picnômetro' },
  { texto: 'Amostra úmida (g)', num: true },
  { texto: 'M₂ (g)', num: true },
  { texto: 'T (°C)', num: true },
  { texto: 'M₁ (g)', num: true },
  { texto: 'Origem de M₁' },
  { texto: 'ρs (g/cm³)', num: true },
];

/** Uma determinação gravada, com o selo da origem de M₁ (onda 322). */
export function linhaDaMes(x: any): LinhaDeLancamento {
  return {
    chave: x.id,
    classe: x.descartada ? 'descartada' : undefined,
    titulo: <>Determinação {x.ordem} · <span className="cod">{x.picnometro ?? '—'}</span></>,
    resultado: x.descartada || x.massa_especifica_solidos == null ? '—'
      : <span className="n">{num(x.massa_especifica_solidos, 4)} g/cm³</span>,
    nota: x.descartada ? `Descartada: ${x.motivo_descarte ?? ''}` : undefined,
    semCartao: [0, 1, 7],
    celulas: [
      x.ordem,
      <span className="cod">{x.picnometro ?? '—'}</span>,
      num(x.massa_amostra_umida, 2),
      num(x.massa_picnometro_solo_agua, 2),
      num(x.temperatura, 1),
      x.massa_picnometro_agua != null ? num(x.massa_picnometro_agua, 2) : '—',
      <SeloDeOrigemM1 x={x} />,
      <b className="fe-w">{x.descartada || x.massa_especifica_solidos == null
        ? '—' : num(x.massa_especifica_solidos, 4)}</b>,
    ],
  };
}

interface RascunhoMes { picnometro: string; umida: string; m2: string; temperatura: string }
const rascunhoMes = (): RascunhoMes => ({ picnometro: '', umida: '', m2: '', temperatura: '' });

/**
 * Repetir a MES na Interpretação: as determinações novas no picnômetro
 * lançadas aqui mesmo, cada uma com o selo da origem de M₁ que o servidor
 * leu na curva (onda 322), e a comparação do ρs inicial × atual.
 *
 * M₁ não se digita aqui: vem da curva, na temperatura. O M₁ declarado é
 * exceção (RN-259) e continua só na etapa Determinações, para quem corrige
 * lançamento travado. A umidade da amostra da repetição vem das cápsulas da
 * etapa 2 (que, com a repetição aberta, são as dela) ou da transcrita.
 */
function RepeticaoMes({ id, d, congelado, opcoesPicnometro, aoMudar }: {
  id: string; d: EnsaioMassaEspecifica; congelado: boolean; opcoesPicnometro: Opcao[];
  aoMudar(): void | Promise<void>;
}) {
  const { pode } = useSessao();
  const interpreta = pode('ensaio.interpretar');
  /* R1: com a repetição aberta, quem interpreta também lança nela. */
  const lanca = pode('ensaio.lancar') || interpreta;
  const [rascunho, setRascunho] = useState<RascunhoMes>(rascunhoMes);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [descartando, setDescartando] = useState<any | null>(null);
  const mexer = (k: keyof RascunhoMes, v: string) => setRascunho((r) => ({ ...r, [k]: v }));

  const umida = paraNumero(rascunho.umida);
  const m2 = paraNumero(rascunho.m2);
  const temperatura = paraNumero(rascunho.temperatura);
  const falta = !rascunho.picnometro ? 'Falta escolher o picnômetro.'
    : umida == null ? 'Falta a massa da amostra úmida.'
    : m2 == null ? 'Falta M₂ (picnômetro + solo + água).'
    : temperatura == null ? 'Falta a temperatura.' : null;

  /* O que acabou de gravar sai do rascunho LOGO (`limpar`), antes das
     releituras: senão o que já se digita na linha seguinte se perderia. */
  const executar = async (
    marca: string, chamada: () => Promise<unknown>, r: UseRepeticao, limpar?: () => void,
  ) => {
    setSalvando(marca); setErro(null);
    /* A ficha relê também: os resumos das etapas contam a repetição (R2). */
    try {
      await chamada();
      limpar?.();
      /* Gravado: o botão se solta JÁ — a releitura não segura o segundo
         clique (que antes caía calado em `salvando`). */
      setSalvando(null);
      await r.recarregar();
      void Promise.resolve(aoMudar()).catch(() => undefined);
      return true;
    }
    catch (e) { setErro(e); return false; }
    finally { setSalvando(null); }
  };

  const lancar = async (r: UseRepeticao) => {
    if (falta || salvando) return;
    const linhas = (r.dados?.atual?.determinacoes ?? []) as any[];
    const ordem = linhas.reduce((m, x) => Math.max(m, Number(x.ordem)), 0) + 1;
    /* O cabeçalho não muda por aqui: vai a balança gravada, a mesma (R1). */
    const balanca = (r.dados?.atual?.balanca ?? r.dados?.inicial?.balanca)?.id;
    await executar('lancar', () => gravarMassaEspecificaDeterminacoes(id, {
      ...(balanca ? { balancaEquipamentoId: balanca } : {}),
      determinacoes: [{
        ordem, picnometroEquipamentoId: rascunho.picnometro,
        massaAmostraUmida: umida, massaPicnometroSoloAgua: m2, temperatura,
      }],
    }), r, () => setRascunho(rascunhoMes()));
  };

  const nova = (r: UseRepeticao): LinhaDeLancamento | null => {
    if (!lanca) return null;
    const ordem = ((r.dados?.atual?.determinacoes ?? []) as any[])
      .reduce((m, x) => Math.max(m, Number(x.ordem)), 0) + 1;
    return {
      chave: 'nova',
      classe: 'nova',
      titulo: `Determinação ${ordem} · nova`,
      semCartao: [0, 5, 6, 7],
      celulas: [
        <span className="sub">{ordem}</span>,
        <EscolhaPorCodigo rotulo="Picnômetro da determinação nova" rotuloOculto
          opcoes={opcoesPicnometro.map(comoCodigo)} valor={rascunho.picnometro}
          aoEscolher={(v) => mexer('picnometro', v)} />,
        <CampoNumerico rotulo="Amostra úmida da determinação nova (g)" rotuloOculto
          value={rascunho.umida} onChange={(e) => mexer('umida', e.target.value)} />,
        <CampoNumerico rotulo="M₂ da determinação nova (g)" rotuloOculto
          value={rascunho.m2} onChange={(e) => mexer('m2', e.target.value)} />,
        <CampoNumerico rotulo="Temperatura da determinação nova (°C)" rotuloOculto
          value={rascunho.temperatura}
          onChange={(e) => mexer('temperatura', e.target.value)} />,
        <span className="sub">da curva</span>,
        <span className="sub">—</span>,
        <span className="sub">—</span>,
        <button type="button" className="botao secundario pequeno fe-gravar-linha" data-gravar-linha=""
          aria-disabled={falta !== null || salvando !== null}
          title={falta ?? 'Grava a determinação na repetição (Enter)'}
          onClick={() => void lancar(r)}>
          {salvando === 'lancar' ? 'Gravando…' : 'Gravar determinação'}
        </button>,
      ],
    };
  };

  const acao = (x: any) => (x.descartada || !lanca ? null : (
    <button type="button" className="botao discreto pequeno"
      aria-label={`Descartar a determinação ${x.ordem}`}
      disabled={salvando !== null} onClick={() => setDescartando(x)}>
      Descartar
    </button>
  ));

  const rodape = (r: UseRepeticao) => {
    const w = r.dados?.atual?.cabecalho?.umidade_higroscopica;
    return (
      <>
        <p className="sub repeticao-dica">
          {lanca
            ? <>M<sub>1</sub> vem da curva do picnômetro, na temperatura, e não se extrapola
              (NBR 17212, 5.2.15); o M<sub>1</sub> declarado é exceção e fica na etapa
              Determinações. </>
            : <>Lançar pede a permissão de lançar dados de ensaio, que o seu perfil não tem. </>}
          Umidade da amostra desta tentativa: {w != null ? `${num(w, 2)} %` : 'ainda sem'}
          {' '}(as cápsulas da etapa Umidade da amostra, ou a transcrita).
        </p>
        <ErroDaApi erro={erro} />
        <JanelaDeDescarte
          alvo={descartando ? `a determinação ${descartando.ordem} · ${descartando.picnometro ?? ''}` : null}
          aoFechar={() => setDescartando(null)}
          aoConfirmar={(motivo) => executar('descarte',
            () => descartarMassaEspecificaDeterminacao(id, descartando.id, motivo), r)}
          salvando={salvando !== null} />
      </>
    );
  };

  return (
    <RepeticaoNumerica id={id} ficha="massa-especifica" titulo="Massa específica dos sólidos"
      podeInterpretar={interpreta} congelado={congelado} aoMudar={aoMudar}
      resultado={RESULTADO_MES} linhas={(f) => f?.determinacoes ?? []}
      colunas={COLUNAS_DA_MES} linha={linhaDaMes} acao={acao} nova={nova} rodape={rodape}
      inicialEmbaixo>
      <ResultadoVigente resultado={RESULTADO_MES} f={d} linhas={d.determinacoes ?? []}
        colunas={COLUNAS_DA_MES} linha={linhaDaMes} rotulo="Determinações do resultado" />
    </RepeticaoNumerica>
  );
}
